import type { FieldMetaData } from "./fieldsMetaData";
import { createFieldMetaData } from "./createFieldMetaData";
import moment from "moment";
import { getAppointmentPhase, getTrainTravelClass } from "../enumutils";
import { GooglePlaceState } from "./groundFields";

export interface TrainFormState {
  trainId: number
  artist: server.dto.ArtistSmall
  phase: server.dto.AppointmentPhase
  travellers: server.dto.ContactSmallV2[]

  bookingReference: string
  travelAgency: string
  notes: string
  trainNumber: string
  carriage: string
  seats: string
  travelClass: server.dto.TrainTravelClass
  files: server.dto.File[]

  departureLocal: moment.Moment
  departureStation: GooglePlaceState
  departurePlatform: string

  arrivalLocal: moment.Moment
  arrivalStation: GooglePlaceState
  arrivalPlatform: string
}

export const mapTrainToState = (
    train: server.dto.Train,
    travellers: server.dto.ContactSmallV2[],
    files: server.dto.File[],
    serverReference: server.dto.ServerReference,
): TrainFormState => ({
  trainId: train.trainId,
  artist: train.artist,
  phase: getAppointmentPhase(train.phase, serverReference),
  travellers: travellers,

  bookingReference: train.bookingReference,
  travelAgency: train.travelAgency,
  notes: train.notes,
  trainNumber: train.trainNumber,
  carriage: train.carriage,
  seats: train.seats,
  travelClass: getTrainTravelClass(train.travelClass, serverReference),
  files,

  departureLocal: moment.utc(train.dateTime.startDisplay),
  departureStation: {
    googlePlaceId: train.departure.googlePlaceId,
    placeIdentifier: train.departure.placeIdentifier,
    name: train.departure.name,
    address: train.departure.address.formatted.join(", "),
    geo: train.departure.latLng,
  },
  departurePlatform: train.departurePlatform,

  arrivalLocal: moment.utc(train.dateTime.endDisplay),
  arrivalStation: {
    googlePlaceId: train.arrival.googlePlaceId,
    placeIdentifier: train.arrival.placeIdentifier,
    name: train.arrival.name,
    address: train.arrival.address.formatted.join(", "),
    geo: train.arrival.latLng,
  },
  arrivalPlatform: train.arrivalPlatform,
});

export const getUpdateTrainDetails = (trainInfo: TrainFormState): server.dto.UpdateTrainInstruction => ({
  arrivalGooglePlaceId: trainInfo.arrivalStation.googlePlaceId,
  arrivalLocal: trainInfo.arrivalLocal.toDate(),
  arrivalPlatform: trainInfo.arrivalPlatform,
  artistId: trainInfo.artist.artistId,
  bookingReference: trainInfo.bookingReference,
  carriage: trainInfo.carriage,
  departureGooglePlaceId: trainInfo.departureStation.googlePlaceId,
  departureLocal: trainInfo.departureLocal.toDate(),
  departurePlatform: trainInfo.departurePlatform,
  notes: trainInfo.notes,
  passengers: trainInfo.travellers.map(t => t.contactId),
  phase: trainInfo.phase.value,
  seats: trainInfo.seats,
  trainId: trainInfo.trainId,
  trainNumber: trainInfo.trainNumber,
  travelAgency: trainInfo.travelAgency,
  travelClass: trainInfo.travelClass?.value ?? "unknown",
});

export interface TrainFieldMetaData {
    artist: FieldMetaData;
    travellers: FieldMetaData;
    phase: FieldMetaData;
    bookingReference: FieldMetaData;
    travelAgency: FieldMetaData;
    notes: FieldMetaData;
    trainNumber: FieldMetaData;
    carriage: FieldMetaData;
    seats: FieldMetaData;
    travelClass: FieldMetaData;

    departureStation: FieldMetaData;
    departurePlatform: FieldMetaData;

    arrivalStation: FieldMetaData;
    arrivalPlatform: FieldMetaData;
}

export const getTrainFieldMetaData = (
    environment: server.dto.SelectedEnvironment,
    item: TrainFormState,
    itemPermissions: server.dto.ObjectPermissions
): TrainFieldMetaData => ({
    artist: createFieldMetaData(
        !item.artist,
        !item.artist && !itemPermissions.canModify, //Hide when empty and read-only
        !itemPermissions.canModify,
    ),

    travellers: createFieldMetaData(
        !item.travellers || item.travellers.length === 0,
        (!item.travellers || item.travellers.length === 0) && !itemPermissions.canModify,
        !itemPermissions.canModify,
    ),

    phase: createFieldMetaData(
        false, //Phase is never empty
        false, //Phase is never hidden
        !itemPermissions.canModify,
    ),

    bookingReference: createFieldMetaData(
        !item.bookingReference,
        !item.bookingReference && !itemPermissions.canModify, //Hide when empty and read-only
        !itemPermissions.canModify,
    ),

    travelAgency: createFieldMetaData(
        !item.travelAgency,
        !item.travelAgency && !itemPermissions.canModify, //Hide when empty and read-only
        !itemPermissions.canModify,
    ),
    notes: createFieldMetaData(
        !item.notes,
        !item.notes && !itemPermissions.canModify, //Hide when empty and read-only
        !itemPermissions.canModify,
    ),
    trainNumber: createFieldMetaData(
        !item.trainNumber,
        !item.trainNumber && !itemPermissions.canModify, //Hide when empty and read-only
        !itemPermissions.canModify,
    ),
    carriage: createFieldMetaData(
        !item.carriage,
        !item.carriage && !itemPermissions.canModify, //Hide when empty and read-only
        !itemPermissions.canModify,
    ),
    seats: createFieldMetaData(
        !item.seats,
        !item.seats && !itemPermissions.canModify, //Hide when empty and read-only
        !itemPermissions.canModify,
    ),
    travelClass: createFieldMetaData(
        !item.travelClass || item.travelClass.value === "unknown",
        (!item.travelClass || item.travelClass.value === "unknown") && !itemPermissions.canModify, //Hide when empty and read-only
        !itemPermissions.canModify,
    ),

    departureStation: createFieldMetaData(
        !item.departureStation || !item.departureStation.googlePlaceId,
        (!item.departureStation || !item.departureStation.googlePlaceId) && !itemPermissions.canModify, //Hide when empty and read-only
        !itemPermissions.canModify,
    ),

    departurePlatform: createFieldMetaData(
        !item.departurePlatform,
        !item.departurePlatform && !itemPermissions.canModify, //Hide when empty and read-only
        !itemPermissions.canModify,
    ),

    arrivalStation: createFieldMetaData(
        !item.arrivalStation || !item.arrivalStation.googlePlaceId,
        (!item.arrivalStation || !item.arrivalStation.googlePlaceId) && !itemPermissions.canModify, //Hide when empty and read-only
        !itemPermissions.canModify,
    ),

    arrivalPlatform: createFieldMetaData(
        !item.arrivalPlatform,
        !item.arrivalPlatform && !itemPermissions.canModify, //Hide when empty and read-only
        !itemPermissions.canModify,
    ),

});
